import * as React from "react";
import * as moment from "moment";
import {Link} from "react-router-dom";
import {
    ALLIANZ,
    ALLIANZ_ESSENTIALS,
    ICacheManager,
    INTERACTION_PROCESS_NB,
    Party,
    Policy,
    PolicyPropertyEnum,
    ProductsEnum,
    Types
} from "@ferry/journey-services";
import {authorize, Overlay} from "@athena-portal/components";
import {lazyInject} from "@athena-portal/core";
import {PluginTemplates} from "../../../utils/PluginTemplates";
import FalconHeader from "../../../components/navigation/FalconHeader";
import {ImgTileLink} from "../../../components/ImgTileLink";
import {Navigate} from "react-router";
import {OverdueInstalmentBanner} from "../../../components/banners/OverdueInstalmentBanner";
import {OtherMissedPaymentBanner} from "../../../components/banners/OtherMissedPaymentBanner";
import {maybePushPurchaseEvent} from "./purchaseEvent";
import {LVDataLayerHelper} from "../../../utils/LVDataLayerHelper";
import {SuccessfulConfirmationModal} from "../../../components/modals/SuccessfulConfirmationModal";
import {formatDate} from "@ferry/plugin-ferry-journey";
import {LvToAllianzUKTransferDashboardBanner} from "../../../components/banners/LvToAllianzUKTransferDashboardBanner";
import {Types as CustomerPortalTypes} from "../../../Types";
import type {ICustomerPortalService} from "../../../services/ICustomerPortalService";
import {InfoBanner, getPrettifiedInteractionBrandName, resourcesHelper, Footer} from "@ferry/portal-core";

interface IState {
    policies?: Policy[];
    expiredPolicies?: Policy[];
    showOldPoliciesTile: boolean;
    policiesProcessed: boolean;
    party?: Party;
    partyLoaded: boolean;
    multiPolicyAccount: boolean;
    isMigratedRenewal: boolean | null;
    latestPolicy?: Policy;
    addedPolicies?: number;
    isLoading: boolean;
}

@authorize
export class MultiPoliciesDashboardPage extends React.Component<{}, IState> {

    private pluginTemplates = new PluginTemplates();
    private showSuccessPopup: string | null;
    private referrerJourney: string | null;

    @lazyInject(CustomerPortalTypes.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    @lazyInject(Types.CacheManager)
    private cacheManager: ICacheManager;

    constructor(props: {}) {
        super(props);

        this.state = {
            policiesProcessed: false,
            partyLoaded: false,
            multiPolicyAccount: false,
            showOldPoliciesTile: false,
            isMigratedRenewal: null,
            isLoading: true
        };
    }

    public componentDidMount(): void {
        this.pluginTemplates.setPageTitle("Dashboard");

        window.sessionStorage.removeItem("product");
        window.sessionStorage.removeItem("payment");

        if (window.sessionStorage.getItem("referrerJourney") === "nb") {
            this.cacheManager.clearCache("getPolicies");
        }

        this.customerPortalService.getPolicies().subscribe((policies: Policy[]) => {
            const referrerJourney = window.sessionStorage.getItem("referrerJourney");
            const showSuccessPopup = window.sessionStorage.getItem("showPopup");
            const prevPoliciesCount = Number(sessionStorage.getItem("prevPoliciesCount") ?? "0");
            window.sessionStorage.setItem("prevPoliciesCount", String(policies.length));

            const addedPolicies = policies.length - prevPoliciesCount;
            const mostRecentPolicy = this.getLatestPolicy(policies);

            if (showSuccessPopup &&
                referrerJourney === INTERACTION_PROCESS_NB &&
                addedPolicies === 2
            ) {
                const interaction = mostRecentPolicy.interaction;
                new LVDataLayerHelper().mapInteractionToDataLayer(interaction);
                maybePushPurchaseEvent(window.LvDataLayer.policyReference);
            }

            if (policies.length > 0) {
                this.customerPortalService.getProfile().subscribe((party: Party) => {
                    this.setState({
                        party,
                        partyLoaded: true,
                        showOldPoliciesTile: party.hasExpiredPolicies,
                    }, () => this.checkIfAllLoaded());
                });
            } else {
                this.setState({
                    partyLoaded: true
                });
            }

            if (policies.length > 1) {
                window.sessionStorage.setItem("multiPolicyAccount", "true");
                this.setState({
                    multiPolicyAccount: true
                });
            }

            this.setState({
                policies,
                policiesProcessed: true,
                latestPolicy: mostRecentPolicy,
                addedPolicies
            }, () => this.checkIfAllLoaded());
        });

        this.customerPortalService.getOldPolicies().subscribe((oldPolicies: Policy[]) => {
            this.setState({
                expiredPolicies: oldPolicies
            }, () => this.checkIfAllLoaded());
        });

        this.customerPortalService.getInteractionByStatus(['MIGRATED_RENEWAL', 'MIGRATED_RENEWAL_ACCEPTED'])
            .subscribe((interaction: any) => {
                this.setState({ isMigratedRenewal: Boolean(interaction) }, () => this.checkIfAllLoaded());
            });
    }

    public render(): React.ReactNode {
        if (this.state.isLoading) {
            return <Overlay show={true} />;
        }

        if (this.state.isMigratedRenewal) {
            return (<Navigate to={`/dashboard/migratedRenewal`}/> );
        }

        const {policies} = this.state;
        const {addedPolicies} = this.state;
        const isDataLoaded = this.isDataLoaded();
        const hasPolicies = this.hasPolicies();
        const latestPolicy = this.state.policies ? this.getLatestPolicy(this.state.policies) : undefined;

        if (policies && (policies.length === 1 || addedPolicies === 1)) {
            if (latestPolicy?.interaction.product === ProductsEnum.DRIVE_AWAY) {
                return this.redirectToSingleProductDashboard(`/dashboard/driveAway/${latestPolicy.policyNumber}`);
            }
            return this.redirectToSingleProductDashboard(`/dashboard/annualMotor/${latestPolicy?.policyNumber}`);
        }

        this.showSuccessPopup = window.sessionStorage.getItem("showPopup");
        this.referrerJourney = window.sessionStorage.getItem("referrerJourney");

        return (
            <React.Fragment>
                <Overlay show={!isDataLoaded} />
                {this.showSuccessPopup && isDataLoaded && !this.state.isMigratedRenewal && (
                    this.renderSuccessfulMultiPoliciesConfirmationModal()
                )}
                <FalconHeader/>
                {!this.state.isMigratedRenewal && (
                    <div className="page-contents dashboard-page">
                        <h1 className="page-heading-white">{this.state.party?.firstName ? `Hi ${this.state.party.firstName}` : "Hi"}</h1>
                        {isDataLoaded && (hasPolicies
                            ? this.renderPageContents()
                            : this.renderNewClientPageContents())}
                    </div>
                )}
                <Footer/>
            </React.Fragment>
        );
    }

    private renderPageContents() {
        return (
            <React.Fragment>
                {this.renderPolicyGrid()}
            </React.Fragment>
        );
    }

    private checkIfAllLoaded() {
        const { policiesProcessed, expiredPolicies, partyLoaded, isMigratedRenewal } = this.state;
        if (policiesProcessed && expiredPolicies && partyLoaded && isMigratedRenewal !== null) {
            this.setState({ isLoading: false });
        }
    }

    private getLatestPolicy(policies: Policy[]): Policy {
        return policies.reduce((latest, current) => {
            const latestDate = moment(latest.interaction.createdOn);
            const currentDate = moment(current.interaction.createdOn);
            return currentDate.isAfter(latestDate) ? current : latest;
        }, policies[0]); // Initial value is the first element of the array
    }

    private getDriveawayPolicy(policies: Policy[],lastPolicy: Policy): Policy | undefined {
        return policies.find(policy => policy.interaction._id === lastPolicy.interaction.driveAwayInteractionId);
    }

    private renderPolicyGrid() {
        const policies = this.state.policies;
        const expiredPolicies = this.state.expiredPolicies
            ?.filter(this.isPolicyNoLongerActive)
            .filter(p => !this.state.policies!.some(po => po.policyNumber === p.policyNumber && po.annualPolicyInfo?.inGracePeriod));
        const inForcePolicies: Policy[] = policies!
            .filter(policy => !expiredPolicies?.some(p => p.policyNumber === policy.policyNumber && p.endDate === policy.endDate))
            .sort((a, b) => moment(a.endDate).diff(b.endDate));
        const showOldPolicies = this.showOldPolicies();

        return (
            <>
                {this.renderPaymentOverdueBanners(inForcePolicies)}
                {inForcePolicies.length > 0 && (
                    <>
                        <p className="mb-1 active-policies-heading margin-bottom-32">
                            Active policies
                        </p>
                        <div className="link-grid mt-0">
                            {this.renderAnnualPolicies(inForcePolicies)}
                        </div>
                    </>
                )}
                <div>
                    {this.renderMenuTiles(showOldPolicies, policies![0])}
                </div>
                <LvToAllianzUKTransferDashboardBanner />
            </>
        );
    }

    private renderAnnualPolicies(policies: Policy[]) {
        let policiesButtons: React.ReactNode[] = [];

        policiesButtons = policiesButtons.concat(this.renderAnnualPolicy(policies));

        return policiesButtons;
    }

    private renderAnnualPolicy(policies: Policy[]) {
        const policiesButtons: React.ReactNode[] = [];

        policies.forEach((policy) => {
            policiesButtons.push(
                <div key={policy.policyNumber} className={`panel ${policy.interaction.brandName}-policy-panel`}>
                    <div className="d-flex p-0">
                        {this.renderMotorPolicy(policy)}
                    </div>
                </div>
            )
        });

        return policiesButtons;
    }

    private renderPaymentOverdueBanners(policies: Policy[]) {
        const banners: React.ReactNode[] = [];
        policies.forEach((policy) => {
            banners.push(this.renderPaymentOverdueBanner(policy))
        });
        return banners;
    }

    private renderPaymentOverdueBanner(policy: Policy) {
        const hasDebt = policy.annualPolicyInfo?.hasDebt ?? false;
        const vehicleName = this.getVehicleName(policy);

        const found = policy?.policyProperties.find(property => PolicyPropertyEnum.REUSE_MAIN_CARD === property.key);
        let reuseCard= found?.value === 'true';

        if (hasDebt && !policy.annualPolicyInfo?.unpaidRenewalInvoiceAmount) {
            return <div className="policy-panel-warning">
                    {
                        (policy.annualPolicyInfo?.overdueMonthlyInvoiceAmount != null && +policy.annualPolicyInfo.overdueMonthlyInvoiceAmount > 0
                            ? <OverdueInstalmentBanner amount={policy.annualPolicyInfo.overdueMonthlyInvoiceAmount}
                                                       unpaidInstallmentIdToAmount={policy.annualPolicyInfo.unpaidInstallmentIdToAmount}
                                                       multiProductPage={true}
                                                       policyNumber={policy.policyNumber}
                                                       product={policy.interaction.product}
                                                       propertyName={vehicleName}
                                                        reuseCard={reuseCard}/>
                            : <OtherMissedPaymentBanner policyNumber={policy.policyNumber}/>)

                    }
                </div>
        }
        return <></>;
    }

    private getVehicleName(policy: Policy): string {
        const vehicle: any = policy.interaction.vehicles[0];
        return vehicle?.make + vehicle?.model + '(' + vehicle?.registrationNumber + ')';
    }

    private renderMotorPolicy(policy: Policy): React.ReactNode {
        const insuredVehicle: any = policy.insuredVehicles[0];
        const redirectUrl: string = policy.interaction.product === ProductsEnum.DRIVE_AWAY ? `/dashboard/driveAway/${policy.policyNumber}` : `/dashboard/annualMotor/${policy.policyNumber}`;
        const productName: string = policy.interaction.product === ProductsEnum.DRIVE_AWAY ? "5-Day Drive Away cover" : "Annual cover";
        const brandName: string = policy.interaction.brandName;

        return (
            <>
                <div className={`float-left brand-icon`} onClick={() => this.navigateToPolicyDashboard(redirectUrl)}>
                    <img className="vehicle-icon" src={`/img/annual/motor/${brandName}.svg`}
                         alt="Brand icon"/>
                </div>
                <div className="d-flex overflow-hidden flex-column"
                     onClick={() => this.navigateToPolicyDashboard(redirectUrl)}>
                    <p className="mb-0 policy-address">{productName}</p>
                    <div className="number-plate-preview margin-top-10 margin-bottom-10">{insuredVehicle.registrationNumber}</div>
                    <p className="mb-0">{insuredVehicle.make} {insuredVehicle.model}</p>
                    <p className="mb-0 policy-expiration">Cover ends on {formatDate(policy.endDate, "DD MMM YYYY, HH:mm")}</p>
                </div>
            </>
        )
    }

    private renderMenuTiles(showOldPolicies: boolean, policy?: any) {
        return <>
            <p className="mb-1 mt-5 menu-heading margin-bottom-32">Menu</p>
            <div className="d-flex flex-wrap menu-items">
                <div className="flexItem quotes">
                    <ImgTileLink id="getQuoteTile"
                                 src={`/${resourcesHelper.THEME_PATH}/img/my-quote.svg`}
                                 alt="Quote icon"
                                 to={`/dashboard/getNewQuote`}
                                 routeState={{
                                     policy
                                 }}
                                 infoModalTitle={"My quote(s)"}
                                 {...this.props}>
                        My quote(s)
                    </ImgTileLink>
                </div>

                <div className="flexItem account-details">
                    <ImgTileLink src={`/${resourcesHelper.THEME_PATH}/img/account-details.svg`} alt="Account details"
                                 to={"/dashboard/multiPolicy/account"}
                                 {...this.props}>
                        Account details
                    </ImgTileLink>
                </div>

                <div className="flexItem marketing-preferences">
                    <ImgTileLink src={`/${resourcesHelper.THEME_PATH}/img/marketing-preferences.svg`} alt="Marketing preferences"
                                 to={"/dashboard/account/marketingPreferences"}
                                 {...this.props}>
                        Marketing preferences
                    </ImgTileLink>
                </div>

                {showOldPolicies &&
                    (<div className="flexItem old-policies">
                            <ImgTileLink src={`/${resourcesHelper.THEME_PATH}/img/old-policies.svg`} alt="Old policies"
                                         to={"/dashboard/oldPolicies"}
                                         {...this.props}>
                                Old policies
                            </ImgTileLink>
                        </div>
                    )}

            </div>
        </>;
    }

    private renderNewClientPageContents(policy?: any, ) {
        const showOldPolicies = this.showOldPolicies();

        return (
            <>
                <InfoBanner wording={<>While you do not have an active insurance policy with us we will keep your details safe and ready for when you need us</>}/>
                <div className="new-client-panel">
                    <div className="new-client-panel-heading">It's empty around here...</div>
                    <div className="flexItem quotes">
                        <ImgTileLink id="getQuoteTile"
                                     src={`/${resourcesHelper.THEME_PATH}/img/my-quote.svg`}
                                     alt="Quotes icon"
                                     to={`/dashboard/getNewQuote`}
                                     routeState={{
                                         policy,
                                     }}
                                     hasInfoButton={true}
                                     infoModalTitle={"My quote(s)"}
                                     infoModalBulletPoints={["View your car insurance quote", "Get a new car insurance quote"]}
                                     infoModalButton="Get a quote"
                                     {...this.props}>
                            My quote(s) <span className="arrow-right"/>
                        </ImgTileLink>
                    </div>
                    {showOldPolicies && (
                        <div className="flexItem old-policies">
                            <ImgTileLink src={`/${resourcesHelper.THEME_PATH}/img/old-policies.svg`}
                                         alt="Old policies"
                                         to={"/dashboard/oldPolicies"}
                                         {...this.props}>
                                Old policies <span className="arrow-right"/>
                            </ImgTileLink>
                        </div>
                    )}
                </div>
            </>
        );
    }

    private redirectToSingleProductDashboard(redirectUrl: string): React.ReactNode {
        return <Navigate to={redirectUrl}/>
    }

    private navigateToPolicyDashboard = (redirectUrl: string) => {
        window.location.assign(redirectUrl);
    }

    private isDataLoaded = (): boolean => {
        return !!this.state.policies
            && this.state.policiesProcessed
            && this.state.partyLoaded;
    };

    private hasPolicies = (): boolean => {
        return !!this.state.policies && this.state.policies?.length !== 0;
    };

    private readonly showOldPolicies = (): boolean => {
        return this.state.showOldPoliciesTile;
    };

    private renderSuccessfulMultiPoliciesConfirmationModal(): React.ReactNode {
        if (this.referrerJourney === INTERACTION_PROCESS_NB) {
            return <SuccessfulConfirmationModal
                headerText={this.state.latestPolicy?.interaction?.factors?.amountToPay === 0 ? "All done" : "Payment complete"}
                modalContent={this.renderSuccessConfirmationPopup()}/>
        }
        return null;
    }

    private readonly isPolicyNoLongerActive = (policy: Policy): boolean => {
        return moment.utc(policy.endDate, moment.ISO_8601).isBefore(moment.utc(), "day");
    };

    private getCoverTierName(coverTier: string): string {
        switch (coverTier) {
            case ALLIANZ_ESSENTIALS:
                return "Essentials";
            case ALLIANZ:
                return "Plus";
            default:
                return coverTier;
        }
    }

    private getAnnualCarInsuranceFullName(policy: Policy): string {
        return getPrettifiedInteractionBrandName(policy.interaction.brandName) + " Annual Car Insurance (" + this.getCoverTierName(policy.interaction.coverTier) + ")";
    }

    private renderSuccessConfirmationPopup(): React.ReactNode {
        const policy: Policy = this.state.latestPolicy!;
        if (!policy) {
            return;
        }

        const driveAwayPolicy = this.state.policies ? this.getDriveawayPolicy(this.state.policies, policy) : undefined;
        const driveAwayStartDate = policy.interaction?.uistate?.relatedInteractionStartDate;
        const annualStartDate = policy.interaction?.headerInfo?.startDate;

        const brandName = getPrettifiedInteractionBrandName(policy.interaction.brandName);
        const amountToPayIs0 = policy.interaction.factors.amountToPay === 0;

        return (
            <div className="text-center">
                <p className="mb-1 font-weight-bold">Thanks {this.state.party?.firstName},</p>
                <p className="mt-4 mb-0"><span className="font-weight-bold">You’re covered.</span></p>
                { driveAwayStartDate ? (
                    <>
                    { amountToPayIs0 ? (
                        <>
                        {!driveAwayPolicy?.interaction.driveAwayOnlyJourney &&
                            <p className="mb-0">5-Day Drive Away cover starts on {formatDate(driveAwayStartDate, "DD MMM YYYY")}</p>
                        }
                        <p>The {this.getAnnualCarInsuranceFullName(policy)} starts on {formatDate(annualStartDate, "DD MMM YYYY")}.</p>
                        </>) : (
                        <>
                        <p className="mt-0 mb-0">
                            <span className="font-weight-bold">Your payment has been taken successfully and your insurance cover is now scheduled to start.</span>
                        </p>
                        {!driveAwayPolicy?.interaction.driveAwayOnlyJourney &&
                            <p className="mb-0">5-Day Drive Away cover starts on {formatDate(driveAwayStartDate, "DD MMM YYYY")}</p>
                        }
                        <p>{this.getAnnualCarInsuranceFullName(policy)} starts on {formatDate(annualStartDate, "DD MMM YYYY")}</p>
                        </>
                    )}
                    </>
                ):(
                    <p className="mt-0 mb-0">
                        <span className="font-weight-bold">Your payment has been taken successfully and your insurance cover is now scheduled to start on {formatDate(annualStartDate, "DD MMM YYYY")}</span>
                    </p>
                )}
                <p className="mt-4 mb-4">
                    When you’re ready head over to your account where you can check out your documents and manage your policy online.
                </p>
                <p className="mb-4">
                    If you do need to contact us then head over to our{" "}
                    <Link className="hyperlink" to={`/dashboard/help/${policy.policyNumber}`}>help</Link>{" "}
                    section for your options.
                </p>
                {
                    amountToPayIs0 ? (
                        <p className="mb-0 font-weight-bold">Enjoy your new car.</p>
                    ) : (
                        <p className="mb-0 font-weight-bold">Enjoy your new car and thank you for insuring with {brandName} car insurance.</p>
                    )

                }
            </div>
        )
    }
}
